import { Button } from '@/components/common/button'
import { Card } from '@/components/common/card'
import { DefinitionStat } from '@/components/common/definitionStat'
import { RoundImage } from '@/components/common/roundImage'
import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import { useFixedTermMarketsQuery } from '@/queries/fixedTermMarkets.query'
import { cn } from '@/utils/cn'
import { getRelatedFixedTermMarkets } from '@/utils/fixedTermMarkets/getRelatedFixedTermMarkets'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getValueColor } from '@/utils/styles/getValueColor'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDate, formatDecimalToPercentage, formatUSD, getTokenImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { Link, generatePath } from 'react-router'

const RELATED_MARKETS_LIMIT = 6

interface Props {
  market: FixedTermMarket
}

export const FixedTermMarketRelatedMarkets = ({ market }: Props) => {
  const { data: markets } = useFixedTermMarketsQuery()
  const { trackEvent } = useMixpanel()
  const relatedMarkets = useMemo(
    () => (markets ? getRelatedFixedTermMarkets(markets, market, RELATED_MARKETS_LIMIT) : undefined),
    [market, markets]
  )

  if (!relatedMarkets?.length) return null

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-medium text-heading-foreground text-lg">Other fixed-term PTs</h2>
      <div className="grid gap-3 md:grid-cols-2">
        {relatedMarkets.map((relatedMarket) => {
          const maturityDate = formatDate({
            timestamp: relatedMarket.expiry * SECOND_IN_MILLISECONDS,
            options: { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' },
          })

          return (
            <Card
              key={`${relatedMarket.network.name}:${relatedMarket.vaultId}`}
              as="article"
              className={cn(
                'group relative flex items-center justify-between gap-3 bg-surface px-4 py-3',
                'ring-inset has-[:focus-visible]:ring'
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <RoundImage
                  src={
                    relatedMarket.accountingAsset.assetLogo ??
                    getTokenImage(relatedMarket.accountingAsset.symbol ?? 'fallback')
                  }
                  fallback={getTokenImage('fallback')}
                  alt={relatedMarket.name}
                  className="size-8 shrink-0 p-1"
                />
                <div className="min-w-0">
                  <Button
                    variant="default"
                    size="default"
                    className={cn(
                      '!ring-0 block min-w-0 max-w-full text-heading-foreground',
                      'before:absolute before:inset-0 before:h-full before:w-full'
                    )}
                    asChild
                  >
                    <Link
                      to={generatePath(routes.fixedTermMarket, {
                        network: relatedMarket.network.name,
                        vaultId: relatedMarket.vaultId,
                      })}
                      prefetch="intent"
                      onClick={() =>
                        trackEvent('Related fixed term market clicked', {
                          network: relatedMarket.network.name,
                          vaultId: relatedMarket.vaultId,
                        })
                      }
                    >
                      <h3 className="truncate font-medium text-sm" title={relatedMarket.name}>
                        {relatedMarket.name}
                      </h3>
                    </Link>
                  </Button>
                  <p className="truncate text-caption-foreground text-xs">Maturity: {maturityDate}</p>
                </div>
              </div>
              <dl className="flex shrink-0 items-center gap-4">
                <DefinitionStat
                  label="Fixed APY"
                  value={formatDecimalToPercentage(relatedMarket.fixedApy)}
                  className="gap-0.5"
                  labelClassName="font-normal"
                  valueClassName={cn('text-sm tabular-nums', getValueColor(relatedMarket.fixedApy))}
                />
                <DefinitionStat
                  label="TVL"
                  value={formatUSD(relatedMarket.tvl.usd)}
                  className="gap-0.5"
                  labelClassName="font-normal"
                  valueClassName="text-sm tabular-nums"
                />
              </dl>
            </Card>
          )
        })}
      </div>
    </section>
  )
}
